import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sprout, Loader2, Flame } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Small flame badge for prospects who opened/clicked a nurture email recently.
export function NurtureWarmBadge({ prospect, testid }) {
  const opens = prospect?.nurture_opens || 0;
  const clicks = prospect?.nurture_clicks || 0;
  if (!prospect?.nurture_last_engaged_at || (opens === 0 && clicks === 0)) return null;
  return (
    <Badge className="bg-[#FFF7D1] text-[#6B5600] border-0 gap-1 shrink-0" title="Engaged with a nurture email recently — good time to call" data-testid={testid}>
      <Flame className="w-3 h-3" /> Warm{clicks > 0 ? ` · ${clicks} click${clicks === 1 ? "" : "s"}` : ""}
    </Badge>
  );
}

export default function AddToNurture({ prospectId, prospectIds, variant = "icon", onEnrolled }) {
  const [open, setOpen] = useState(false);
  const [seqs, setSeqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const ids = prospectIds || (prospectId ? [prospectId] : []);

  const openDialog = async (e) => {
    e?.stopPropagation?.();
    setOpen(true);
    setLoading(true);
    try {
      const r = await axios.get(`${API}/nurture/sequences`);
      setSeqs((r.data.sequences || []).filter((s) => s.active));
    } catch { toast.error("Couldn't load sequences"); }
    finally { setLoading(false); }
  };

  const enroll = async (seq) => {
    setBusyId(seq.id);
    try {
      const r = await axios.post(`${API}/nurture/sequences/${seq.id}/enroll`, { prospect_ids: ids });
      if (r.data.enrolled > 0) toast.success(`Added to "${seq.name}"`);
      else toast.info(r.data.skipped ? "Already enrolled or unsubscribed" : "Nothing to enrol");
      setOpen(false);
      onEnrolled?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Enrol failed"); }
    finally { setBusyId(null); }
  };

  return (
    <>
      {variant === "icon" ? (
        <Button size="icon" variant="outline" title="Add to nurture"
          className="h-8 w-8 border-[#DAD5CA] bg-transparent text-[#0E6B3F] hover:bg-[#E3F3E9] hover:text-[#0E6B3F]"
          onClick={openDialog} data-testid={`sales-nurture-${prospectId}`}>
          <Sprout className="w-3.5 h-3.5" />
        </Button>
      ) : (
        <Button size="sm" variant="outline"
          className="border-[#E3F3E9] bg-transparent text-[#0E6B3F] hover:bg-[#E3F3E9]"
          onClick={openDialog} data-testid="sales-detail-nurture">
          <Sprout className="w-4 h-4 mr-1" /> Add to Nurture
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-md" data-testid="add-to-nurture-dialog">
          <DialogHeader><DialogTitle>Add to a nurture sequence</DialogTitle></DialogHeader>
          <p className="text-xs text-[#59554D] -mt-2">
            {ids.length > 1 ? `${ids.length} prospects` : "This prospect"} will start receiving the sequence's
            value emails from their assigned rep.
          </p>
          <div className="space-y-1.5 max-h-72 overflow-y-auto mt-1">
            {loading && <div className="text-xs text-[#8A857A] py-4 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>}
            {!loading && seqs.length === 0 && (
              <div className="text-xs text-[#8A857A] py-4 text-center">
                No active sequences. Create one under Sales → Nurture first.
              </div>
            )}
            {seqs.map((s) => (
              <button key={s.id} onClick={() => enroll(s)} disabled={busyId === s.id}
                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded border border-[#DAD5CA] bg-[#FFFFFF] hover:border-[#E3F3E9] text-left transition-colors disabled:opacity-50"
                data-testid={`add-nurture-seq-${s.id}`}>
                <div className="min-w-0">
                  <div className="text-sm text-[#1A1A1A] truncate">{s.name}</div>
                  <div className="text-xs text-[#8A857A]">{(s.steps || []).length} step{(s.steps || []).length === 1 ? "" : "s"}</div>
                </div>
                {busyId === s.id ? <Loader2 className="w-4 h-4 animate-spin text-[#0E6B3F]" /> : <Sprout className="w-4 h-4 text-[#0E6B3F]" />}
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-[#59554D]">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
